<script setup lang="ts">
/**
 * Aura — Share Certificate (ordinary shares). 원본 docx 문구 그대로 재현.
 *   템플릿 변수(소문자 snake): company_name · company_registration_no · certificate_no · number_of_shares ·
 *   number_of_shares_in_words(숫자에서 자동, 수정 가능) · registered_office_address · shareholder_name ·
 *   date_of_issue · 서명 2명(고객 원본 Share Cert No. 1 — 점선 + 이름 + 직책, 오른쪽):
 *   director_name / director_designation(이사 1명) · second_signer_name / second_signer_designation(이사 또는 비서 1명).
 */
import { watch } from 'vue';
import CddVarField from '../cdd/CddVarField.vue';
import { useAgmFill } from '../cdd/useCddFill';
import { numberToWords } from '../utils/normalizeShareCertForWord';
import './shareCertDoc.css';

const fill = useAgmFill();

// 주식수 → 영문 표기 자동. 운영자가 직접 고친 값(마지막 자동값과 다름)은 보존.
let lastAutoWords = '';
watch(
  () => fill.values.number_of_shares,
  (n) => {
    const auto = numberToWords(n);
    const cur = String(fill.values.number_of_shares_in_words ?? '').trim();
    if (!cur || cur === lastAutoWords) fill.setValue('number_of_shares_in_words', auto);
    lastAutoWords = auto;
  },
  { immediate: true },
);
</script>

<template>
  <div class="agm-doc share-cert">
    <table class="sc-head">
      <tbody>
        <tr>
          <td class="sc-side">
            <div class="sc-side-lbl">Certificate No.</div>
            <div class="sc-side-val">*<CddVarField field-key="certificate_no" label="Certificate No." />*</div>
          </td>
          <td class="sc-mid">
            <div class="sc-company"><CddVarField field-key="company_name" label="Company Name" /></div>
            <div>(the “Company”)</div>
            <div>(Company Registration No. <CddVarField field-key="company_registration_no" label="Company Registration No." />)</div>
          </td>
          <td class="sc-side">
            <div class="sc-side-lbl">No. of Shares</div>
            <div class="sc-side-val">*<CddVarField field-key="number_of_shares" label="No. of Shares" />*</div>
          </td>
        </tr>
      </tbody>
    </table>

    <p class="doc-p center">Incorporated in the Republic of Singapore under the Companies Act 1967</p>
    <p class="doc-p center">REGISTERED OFFICE: <CddVarField field-key="registered_office_address" label="Registered Office Address" /></p>

    <p class="doc-p sc-body">
      This is to certify that <CddVarField field-key="shareholder_name" label="Shareholder Name" />
      is the Registered Holder of <CddVarField field-key="number_of_shares" label="No. of Shares" />
      (<CddVarField field-key="number_of_shares_in_words" label="No. of Shares in Words" />)
      Ordinary Share fully paid in <CddVarField field-key="company_name" label="Company Name" />
      subject to the Constitution of the Company on <CddVarField field-key="date_of_issue" label="Date of Issue" />
    </p>

    <!-- 서명 2명(고객 회신) — 첫 칸 이사 1명, 둘째 칸 이사 또는 비서 1명. 고객 원본처럼 점선 + 이름(굵게) + 직책, 오른쪽. -->
    <div class="sc-signs">
      <div class="sc-sign">
        <div class="sc-dash">-----------------------------------------------------------</div>
        <div class="sc-sign-name"><CddVarField field-key="director_name" label="Director Name" /></div>
        <div><CddVarField field-key="director_designation" label="Designation" /></div>
      </div>
      <div class="sc-sign">
        <div class="sc-dash">-----------------------------------------------------------</div>
        <div class="sc-sign-name"><CddVarField field-key="second_signer_name" label="Director or Secretary Name" /></div>
        <div><CddVarField field-key="second_signer_designation" label="Designation (Director / Secretary)" /></div>
      </div>
    </div>

    <p class="doc-note sc-note">
      NOTE: No transfer of any portion of the shares comprised in this<br />
      Certificate will be registered unless accompanied by this Certificate
    </p>
  </div>
</template>

<style scoped>
.sc-head { width: 100%; border-collapse: collapse; margin-bottom: 14px; }
.sc-head td { vertical-align: top; padding: 4px 6px; }
.sc-side { width: 22%; text-align: center; }
.sc-side-lbl { font-weight: 700; }
.sc-side-val { margin-top: 6px; font-weight: 700; }
.sc-mid { text-align: center; }
.sc-company { font-weight: 800; font-size: 14px; margin-bottom: 2px; }
.sc-body { margin-top: 16px; text-align: justify; line-height: 1.8; }
.sc-signs { display: flex; flex-direction: column; gap: 34px; margin-top: 40px; }
/* 고객 원본처럼 서명 자리를 오른쪽 끝에 — 점선 · 이름(굵게) · 직책 오른쪽 정렬 */
.sc-sign { margin-left: auto; text-align: right; line-height: 1.7; }
.sc-dash { letter-spacing: -0.5px; color: #333; }
.sc-sign-name { font-weight: 700; }
.sc-note { margin-top: 30px; }
</style>
